import React, { useEffect } from "react";
import { pusherConfig } from "../config/pusher";
import Pusher from "pusher-js";

interface PusherParams {
  siteId: string;
  broadcastChannel: string;
  broadcastAs: string;
}

interface PusherImportParams extends PusherParams {}
interface PusherExportParams extends PusherParams {}

// Pusher.logToConsole = true;
let pusher = new Pusher(pusherConfig.app_key, {
  cluster: pusherConfig.app_cluster,
});

/**
 * The function `usePusherImportProgress` is a custom hook in TypeScript that sets up a subscription to
 * a Pusher channel and updates the import progress based on the received data.
 * @param {PusherImportParams}  - - `siteId`: The ID of the site for which the import progress is being
 * tracked.
 */
export function usePusherImportProgress({ siteId, broadcastChannel, broadcastAs }: PusherImportParams) {
  const [importInProgress, setImportInProgress] = React.useState(false);
  const [progress, setProgress] = React.useState(0);

  useEffect(() => {
    if (siteId !== "") {
      // Channel
      let channel = pusher.subscribe(broadcastChannel);

      channel.bind(broadcastAs, function (data: any) {
        setImportInProgress(true);
        let percentage = data.progressed_percentage;
        setProgress(percentage);
      });
    }
  }, [siteId]);

  return {
    setImportInProgress,
    importInProgress,
    setProgress,
    progress,
  };
}

/**
 * The `usePusherBulkExportProgress` function is a custom React hook that manages the progress and
 * status of a bulk export process using Pusher for real-time updates.
 * @param {PusherExportParams}  - - `siteId`: The ID of the site for which the bulk export progress is
 * being tracked.
 */
export function usePusherBulkExportProgress({ siteId, broadcastChannel, broadcastAs }: PusherExportParams) {
  const [exportInProgress, setExportInProgress] = React.useState(false);
  const [exportProgress, setExportProgress] = React.useState(0);
  const [bulkExportStatus, setBulkExportStatus] = React.useState("");
  const [totalProducts, setTotalProducts] = React.useState(0);

  useEffect(() => {
    if (siteId !== "") {
      // Channel
      let channel = pusher.subscribe(broadcastChannel);

      channel.bind(broadcastAs, function (data: any) {
        let itemProgress = data.progressed_item;
        setExportInProgress(true);
        setExportProgress(itemProgress);
        setBulkExportStatus(data.export_status);
        setTotalProducts(data.total_products);
      });
    }
  }, [siteId]);

  return {
    setExportInProgress,
    exportInProgress,
    setExportProgress,
    exportProgress,
    setBulkExportStatus,
    bulkExportStatus,
    setTotalProducts,
    totalProducts,
  };
}

/**
 * The function `usePusherExportProgress` is a custom hook that subscribes to a Pusher channel and
 * updates the export progress and status of a single product export.
 * @param {PusherExportParams}  - - `siteId`: The ID of the site for which the export progress is being
 * tracked.
 */
export function usePusherExportProgress({ siteId, broadcastChannel, broadcastAs }: PusherExportParams) {
  const [exportProductInProgress, setExportProductInProgress] = React.useState(false);
  const [exportProductStatus, setExportProductStatus] = React.useState("");

  useEffect(() => {
    if (siteId !== "") {
      let channel = pusher.subscribe(broadcastChannel);

      channel.bind(broadcastAs, function (data: any) {
        setExportProductInProgress(true);
        setExportProductStatus(data.export_status);
      });
    }
  }, [siteId]);

  return {
    exportProductInProgress,
    setExportProductInProgress,
    exportProductStatus,
    setExportProductStatus,
  };
}
